import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Tema da interface: claro, escuro ou o do sistema (acompanha o aparelho).
 * A preferência fica no navegador; o tema resolvido vai para o <html>
 * (data-theme + color-scheme) e para o Kumo (data-mode).
 */
export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const KEY = 'ingrave-theme';
const media = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null);

function readPreference(): ThemePreference {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref !== 'system') return pref;
  return media()?.matches ? 'dark' : 'light';
}

/** Aplica no <html> antes do React montar (evita piscar branco no modo escuro) */
export function applyThemeEarly() {
  const t = resolveTheme(readPreference());
  document.documentElement.dataset.theme = t;
  document.documentElement.dataset.mode = t;
  document.documentElement.style.colorScheme = t;
}

interface ThemeValue { preference: ThemePreference; theme: ResolvedTheme; setPreference: (p: ThemePreference) => void }
const ThemeContext = createContext<ThemeValue>({ preference: 'system', theme: 'light', setPreference: () => undefined });

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPref] = useState<ThemePreference>(readPreference);
  const [systemDark, setSystemDark] = useState(() => !!media()?.matches);

  useEffect(() => {
    const m = media();
    if (!m) return;
    const on = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);

  const theme: ResolvedTheme = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    // O Kumo escolhe o tema por data-mode
    document.documentElement.dataset.mode = theme;
    document.documentElement.style.colorScheme = theme;
  }, [theme]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPref(p);
    try { localStorage.setItem(KEY, p); } catch { /* navegador sem armazenamento */ }
  }, []);

  const value = useMemo(() => ({ preference, theme, setPreference }), [preference, theme, setPreference]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
